/**
 * Background tiles — the curated backgrounds an organization (or corp) picks
 * from instead of a free surface picker (rebrand v2, spec §3).
 *
 * Nine tiles carry a FIXED five-surface ladder, exactly the set a tenant pin
 * stores. The tenth, `brand-dark`, is the look every org has today: the dark
 * ladder toned from `primary-main`, so it has no ladder of its own.
 *
 * The values are curated, not generated: the hex table in the spec is
 * authoritative, and `surface-tiles.spec.ts` pins it together with the
 * readability guarantees measured on it — every non-brand contrast pair and a
 * readable mark for every status family, on every tile.
 *
 * Only a tile id or an org's own colour (`CustomSurfaceColor`) is stored, under
 * `THEME_SURFACE_TILE_KEY`; the ladder is put in place by the single theme
 * merge, `mergeOrgTheme`, at the org layer — a tile's fixed one, or the one
 * derived from the colour (`customSurfaceLadder`).
 */

/**
 * The key an organization's stored theme record uses for its background: a
 * tile id, or a lowercase `#rrggbb` custom colour (`CustomSurfaceColor`).
 *
 * Same rules as `THEME_EFFECT_GLOW_KEY`: deliberately not `--mfw-`-prefixed,
 * never a CSS variable, and an unknown stored value degrades to "absent".
 * Org/corp only — the tenant keeps its free surface pickers.
 */
export const THEME_SURFACE_TILE_KEY = 'surface';

/** Every catalog tile, in the order the editor offers them. */
export const SURFACE_TILE_IDS = [
  'brand-dark',
  'white',
  'off-white',
  'beige',
  'dark-beige',
  'light-navy',
  'dark-navy',
  'light-grey',
  'dark-grey',
  'black',
] as const;

export type SurfaceTileId = (typeof SURFACE_TILE_IDS)[number];

/**
 * The five surface vars a tile sets — the same set as
 * `SURFACE_THEME_VAR_NAMES`, as a type, so a ladder cannot miss one.
 */
export type SurfaceVarName =
  | '--mfw-palette-background-default'
  | '--mfw-palette-background-paper'
  | '--mfw-palette-background-neutral'
  | '--mfw-palette-background-table-0'
  | '--mfw-palette-background-table-1';

export interface SurfaceTileDefinition {
  /** Which end of the ladder the tile sits on; text follows it. */
  mode: 'light' | 'dark';
  /** The fixed surfaces, or `null` for `brand-dark` (toned from the brand). */
  ladder: Record<SurfaceVarName, string> | null;
  /** @mfw/translations message id of the tile's name. */
  labelKey: string;
}

const ladder = (
  defaultColor: string,
  paper: string,
  neutral: string,
  table0: string,
  table1: string,
) => ({
  '--mfw-palette-background-default': defaultColor,
  '--mfw-palette-background-paper': paper,
  '--mfw-palette-background-neutral': neutral,
  '--mfw-palette-background-table-0': table0,
  '--mfw-palette-background-table-1': table1,
});

export const SURFACE_TILES: Record<SurfaceTileId, SurfaceTileDefinition> = {
  'brand-dark': {
    mode: 'dark',
    ladder: null,
    labelKey: 'themeVars.surfaceTileBrandDark',
  },
  white: {
    mode: 'light',
    ladder: ladder('#ffffff', '#ffffff', '#f2f4f5', '#ffffff', '#f9fafb'),
    labelKey: 'themeVars.surfaceTileWhite',
  },
  'off-white': {
    mode: 'light',
    ladder: ladder('#f9f6f2', '#ffffff', '#edebe6', '#ffffff', '#f4f1ed'),
    labelKey: 'themeVars.surfaceTileOffWhite',
  },
  beige: {
    mode: 'light',
    ladder: ladder('#f5e9da', '#f9f2e9', '#ebddcb', '#f9f2e9', '#f0e4d5'),
    labelKey: 'themeVars.surfaceTileBeige',
  },
  'dark-beige': {
    mode: 'dark',
    ladder: ladder('#282019', '#342c24', '#443b33', '#443b33', '#53483e'),
    labelKey: 'themeVars.surfaceTileDarkBeige',
  },
  'light-navy': {
    mode: 'light',
    ladder: ladder('#e2ecf7', '#eef4fb', '#d5e1ee', '#eef4fb', '#dee7f2'),
    labelKey: 'themeVars.surfaceTileLightNavy',
  },
  'dark-navy': {
    mode: 'dark',
    ladder: ladder('#0a1628', '#152235', '#233145', '#233145', '#2d3e56'),
    labelKey: 'themeVars.surfaceTileDarkNavy',
  },
  'light-grey': {
    mode: 'light',
    ladder: ladder('#e6e8ea', '#eff0f2', '#dadddf', '#eff0f2', '#e1e3e5'),
    labelKey: 'themeVars.surfaceTileLightGrey',
  },
  'dark-grey': {
    mode: 'dark',
    ladder: ladder('#24272a', '#303337', '#404347', '#404347', '#4c5055'),
    labelKey: 'themeVars.surfaceTileDarkGrey',
  },
  black: {
    mode: 'dark',
    ladder: ladder('#000000', '#121212', '#1e1e1e', '#1e1e1e', '#292929'),
    labelKey: 'themeVars.surfaceTileBlack',
  },
};

/**
 * The tiles a NEW choice may land on — the release gate (spec §9), shared by
 * the editor and the org/corp services so the two cannot disagree.
 *
 * The gate is OPEN: light-mode packages E and F shipped, so all ten tiles are
 * offered. It stays as the single switch for a future tile — a literal list,
 * not an alias of `SURFACE_TILE_IDS`, so a tile added to the catalog stays
 * unavailable until it is listed here, in the same build as the editor and
 * the services (deploy FE and BE together).
 *
 * It gates choosing, not resolving: a tile that is already stored still
 * resolves through `resolveSurfaceTile` whether or not it is listed here.
 */
export const ENABLED_SURFACE_TILE_IDS: readonly SurfaceTileId[] = [
  'brand-dark',
  'white',
  'off-white',
  'beige',
  'dark-beige',
  'light-navy',
  'dark-navy',
  'light-grey',
  'dark-grey',
  'black',
];

const isSurfaceTileId = (value: unknown): value is SurfaceTileId =>
  typeof value === 'string' &&
  (SURFACE_TILE_IDS as readonly string[]).includes(value);

/**
 * The tile an org/corp theme record explicitly chose, or `undefined` for
 * none. Reads the RAW record: `mergeOrgTheme`'s editable-var filter would have
 * dropped the key already.
 *
 * A stored row is data, not a promise, so anything that is not a catalog id —
 * `null`, a number, an unknown or mis-cased string — is "absent", which keeps
 * today's behaviour, rather than a guess at what was meant.
 */
export function resolveSurfaceTile(theme?: Record<string, unknown> | null) {
  const stored = theme?.[THEME_SURFACE_TILE_KEY];
  return isSurfaceTileId(stored) ? stored : undefined;
}

/** A background colour an org picked itself: `#rrggbb`, stored lowercase. */
export type CustomSurfaceColor = `#${string}`;

/** What the stored `surface` value says: a catalog tile or a custom colour. */
export type SurfaceChoice =
  | { kind: 'tile'; id: SurfaceTileId }
  | { kind: 'custom'; color: CustomSurfaceColor };

/** Any case on the way in; `resolveSurfaceChoice` and the DTO lowercase it. */
export const isCustomSurfaceColor = (
  value: unknown,
): value is CustomSurfaceColor =>
  typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);

/**
 * What an org/corp theme record chose for its background, or `undefined` for
 * nothing: a catalog tile, or a custom colour (lowercased — a legacy or
 * hand-edited row may hold any case). Reads the RAW record, like
 * `resolveSurfaceTile`, and degrades anything else to "absent".
 *
 * Unlike a tile, a custom colour is never behind the release gate
 * (`ENABLED_SURFACE_TILE_IDS`): the gate lists tiles only.
 */
export function resolveSurfaceChoice(
  theme?: Record<string, unknown> | null,
): SurfaceChoice | undefined {
  const stored = theme?.[THEME_SURFACE_TILE_KEY];
  if (isSurfaceTileId(stored)) return { kind: 'tile', id: stored };
  if (isCustomSurfaceColor(stored)) {
    return {
      kind: 'custom',
      color: stored.toLowerCase() as CustomSurfaceColor,
    };
  }
  return undefined;
}
